import { Injectable } from '@angular/core';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, tap } from 'rxjs';

import {
  DprAttachmentsInfoSync,
  DprCard,
  DprCards,
  DprDownloadFilters,
} from '@dpr/models';
import { Attachment, DprDetails } from '@shared/models/dpr';
import { PersistenceService } from '@shared/services/persistence';
import { AppFacade } from '@shared/state/facades';
import {
  DprBasePersistenceService,
  DprLocalPersistenceService,
  DprRemotePersistenceService,
} from './persistence';

import { DateFilter } from '@shared/models';
import { convertToDateWithoutTimeZoneSuffix } from '@shared/utils';
import { GenericHttpClientService } from '@shared/services';
import { HttpDprContext } from '@shared/enums';

@UntilDestroy()
@Injectable()
export class DprService {
  action: DprBasePersistenceService;

  constructor(
    private genericHttpClientService: GenericHttpClientService,
    private appFacade: AppFacade,
    private persistenceService: PersistenceService,
    private dprRemotePersistenceService: DprRemotePersistenceService,
    private dprLocalPersistenceService: DprLocalPersistenceService
  ) {
    this.init();
  }

  getDprCards(filters: HttpParams): Observable<DprCards> {
    return this.action.getDprCards(filters);
  }

  getAvailableDates(): Observable<DateFilter> {
    return this.genericHttpClientService.get<DateFilter>({
      context: HttpDprContext.GetAvailableDates,
    });
  }

  getUnlockedDprDates(): Observable<DateFilter> {
    return this.genericHttpClientService.get<DateFilter>({
      context: HttpDprContext.GetUnlockedDprDates,
    });
  }

  uploadSignedDpr(
    dprDetailId: number | string,
    subprojectId: number,
    attachment: Attachment
  ): Observable<void> {
    return this.genericHttpClientService.post<void>({
      context: HttpDprContext.UploadSignedDpr,
      body: { dprDetailId, subprojectId, attachment },
    });
  }

  getDprAttachmentsInfo(
    downloadFilters: DprDownloadFilters
  ): Observable<HttpResponse<DprAttachmentsInfoSync>> {
    const { startDate, endDate, ...paramFlags } = downloadFilters; // take all properties, except the dates
    const params = new HttpParams({
      fromObject: {
        ...paramFlags,
        startDate: convertToDateWithoutTimeZoneSuffix(startDate),
        endDate: convertToDateWithoutTimeZoneSuffix(endDate),
      },
    });

    return this.genericHttpClientService.get<
      HttpResponse<DprAttachmentsInfoSync>
    >({
      context: HttpDprContext.GetDprAttachmentsInfo,
      observeResponse: true,
      params,
    });
  }

  lockDpr(
    dprDetailId: number | string,
    subprojectId: number,
    lockDate: string
  ): Observable<DprDetails> {
    return this.action.lockDpr(dprDetailId, subprojectId, lockDate);
  }

  unlockDpr(dprDetailId: number | string): Observable<number | string> {
    return this.action.unlockDpr(dprDetailId);
  }

  getLastThreeDprs(): Observable<DprDetails[]> {
    return this.persistenceService.action.getLastThreeDprs();
  }

  createDpr(subprojectId: number, date: Date): Observable<DprCard> {
    return this.action.createDpr(subprojectId, date);
  }

  private init(): void {
    this.appFacade.query.isAppOnline$
      .pipe(
        untilDestroyed(this),
        tap((isAppOnline) => {
          this.action = isAppOnline
            ? this.dprRemotePersistenceService
            : this.dprLocalPersistenceService;
        })
      )
      .subscribe();
  }
}
